import { selectTaskListAriaText } from './FolderPicker.locstring.json';
import { defaultTasksFolderName } from '../../strings.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47647 */
import * as React from 'react';
import getSelectedFolderView from '../../selectors/getSelectedFolderView';
import getTodoFolderStore from '../../selectors/getTodoFolderStore';

import TodoFolder from '../../store/schema/TodoFolder';
import { ActionButton, CommandButton } from 'office-ui-fabric-react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { DirectionalHint, IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { observer } from 'mobx-react';
import { switchFolder } from '../../actions/todoPanelStoreActions';

const styles = require('./FolderPicker.scss');
const classNames = require('classnames/bind').bind(styles);

export interface FolderPickerStyles {
    /**
     * Optional class name to be added to the folders command button.
     */
    commandButtonClassName?: string;
    /**
     * Optional class name to be added to the folders command button flex container.
     */
    commandButtonFlexContainerClassName?: string;
    /**
     * Optional class name to be added to the folders command button text label.
     */
    commandButtonTextLabelClassName?: string;
    /**
     * Optional class name to be added to the folders command button icon.
     */
    commandButtonIconClassName?: string;
}

export interface FolderPickerProps {
    styles?: FolderPickerStyles;
}

@observer
export default class FolderPicker extends React.Component<FolderPickerProps, {}> {
    render() {
        const folderStore = getTodoFolderStore();

        if (!folderStore.orderedList || folderStore.orderedList.length == 0) {
            return this.renderNoLoadedFolders();
        }

        if (folderStore.orderedList.length == 1) {
            return this.renderSingleFolder();
        }

        return this.renderMultipleFolders();
    }

    renderNoLoadedFolders(): JSX.Element {
        return (
            <CommandButton
                className={classNames(
                    styles.multipleFoldersCommandButton,
                    this.props.styles && this.props.styles.commandButtonClassName
                )}
                text={loc(defaultTasksFolderName)}
                onRenderText={this.onRenderText(loc(defaultTasksFolderName))}
                styles={{
                    flexContainer: classNames(
                        styles.commandButtonFlexContainer,
                        this.props.styles && this.props.styles.commandButtonFlexContainerClassName
                    ),
                    textContainer: styles.commandButtonTextContainer,
                }}
            />
        );
    }

    renderSingleFolder(): JSX.Element {
        const folderStore = getTodoFolderStore();
        const folder: TodoFolder = folderStore.folders.get(folderStore.orderedList[0]);

        if (!folder) {
            return this.renderNoLoadedFolders();
        }

        return (
            <CommandButton
                className={classNames(
                    styles.multipleFoldersCommandButton,
                    this.props.styles && this.props.styles.commandButtonClassName
                )}
                text={folder.Name}
                onRenderText={this.onRenderText(folder.Name)}
                styles={{
                    flexContainer: classNames(
                        styles.commandButtonFlexContainer,
                        this.props.styles && this.props.styles.commandButtonFlexContainerClassName
                    ),
                    textContainer: styles.commandButtonTextContainer,
                }}
            />
        );
    }

    renderMultipleFolders(): JSX.Element {
        const folderStore = getTodoFolderStore();
        const selectedFolderView = getSelectedFolderView();
        const selectedFolder = folderStore.folders.get(selectedFolderView.folderId);

        if (!selectedFolder) {
            return this.renderNoLoadedFolders();
        }

        let folderMenuItems: IContextualMenuItem[] = this.createMenuItemsForFolders();
        return (
            <CommandButton
                className={classNames(
                    styles.multipleFoldersCommandButton,
                    this.props.styles && this.props.styles.commandButtonClassName
                )}
                text={selectedFolder.Name}
                menuProps={{
                    directionalHint: DirectionalHint.bottomLeftEdge,
                    items: folderMenuItems,
                    isBeakVisible: false,
                }}
                onRenderMenuIcon={this.onRenderMenuIcon}
                ariaLabel={loc(selectTaskListAriaText)}
                onRenderText={this.onRenderText(selectedFolder.Name)}
                styles={{
                    flexContainer: classNames(
                        styles.commandButtonFlexContainer,
                        this.props.styles && this.props.styles.commandButtonFlexContainerClassName
                    ),
                    textContainer: styles.commandButtonTextContainer,
                    rootExpanded: styles.foldersCommandButtonExpanded,
                }}
            />
        );
    }

    /**
     * Renders the icon within the command button
     */
    private onRenderMenuIcon = (): JSX.Element => {
        return (
            <Icon
                iconName={ControlIcons.ChevronDown}
                className={classNames(
                    styles.commandButtonIcon,
                    this.props.styles && this.props.styles.commandButtonIconClassName
                )}
            />
        );
    };

    /**
     * Renders the text within the command button
     */
    private onRenderText = (folderName: string) => () => {
        return (
            <div
                className={classNames(
                    styles.commandButtonTextLabel,
                    this.props.styles && this.props.styles.commandButtonTextLabelClassName
                )}>
                {folderName}
            </div>
        );
    };

    /**
     *  Create the options for the dropdown
     *  @return menu items of type IContextualMenuItem[]
     */
    private createMenuItemsForFolders = () => {
        const folderStore = getTodoFolderStore();
        const menuItems: IContextualMenuItem[] = [];

        folderStore.orderedList.map(folderId => {
            const folder = folderStore.folders.get(folderId);

            menuItems.push({
                key: folderId,
                name: folder.Name, // this name won't show due to custom onRender call
                data: { folderId: folderId },
                className: styles.menuItemResultText,
                onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) => {
                    return this.renderContextMenuItem(folder, dismissMenu);
                },
            });
        });

        return menuItems;
    };

    private renderContextMenuItem = (
        folder: TodoFolder,
        dismissMenu: (ev?: any, dismissAll?: boolean) => void
    ): React.ReactNode => {
        const selectedFolderView = getSelectedFolderView();
        const showSelected =
            selectedFolderView === null
                ? folder.IsDefaultFolder
                : selectedFolderView.folderId === folder.Id;

        return (
            <ActionButton
                key={folder.Id}
                data-is-focusable={true}
                className={classNames(styles.menuItemButton, showSelected && styles.showSelected)}
                styles={{
                    flexContainer: styles.menuItemButtonFlexContainer,
                }}
                onClick={() => {
                    this.onMenuItemClick(folder);
                    dismissMenu();
                }}>
                <Icon iconName={this.getIconForFolder(folder)} className={styles.menuItemIcon} />
                <span className={styles.menuItemText}> {folder.Name} </span>
                {folder.IsSharedFolder && (
                    <Icon iconName={ControlIcons.People} className={styles.menuItemIconShared} />
                )}
            </ActionButton>
        );
    };

    private getIconForFolder(folder: TodoFolder): string {
        if (folder.IsDefaultFolder) {
            return ControlIcons.Home;
        }

        if (folder.FolderType === 'Commitments') {
            return ControlIcons.OutlookLogoInverse;
        } else if (folder.FolderType === 'FlaggedEmails') {
            return ControlIcons.Flag;
        }

        return ControlIcons.BulletedList;
    }

    /**
     * Callback issued when the menu item is selected
     */
    onMenuItemClick(folder: TodoFolder) {
        switchFolder(folder.Id);
    }
}
